import { TextField } from '@ovComponents/2-component/textField/textField';
import {
  getDecimalPlaceholder,
  getLocaleNumberSeparators,
  normalizeDecimalInput,
} from '@utils/common';
import { t } from 'i18next';
import { useNumericInputDraft } from './useNumericInputDraft';

interface NumberFieldProps extends React.ComponentProps<typeof TextField> {
  number: string;
  setNumber: (number: string) => void;
  label?: string;
  onBlur?: any;
  error?: any;
  errorText?: any;
  sx?: any;
  disabled?: boolean;
  locked?: boolean;
  infoTooltip?: string;
  decimalPlaces?: number;
  unlimitedDecimals?: boolean;
  testId?: string;
  trailingIcon?: 'percent' | 'calendar' | undefined;
  /** Ignored when `inputType` is 'decimal', since that input has no native spinners. */
  showArrows?: boolean;
  /**
   * 'number' (default) renders a native number input. 'decimal' renders a text
   * input that uses the app language's decimal separator (e.g. "12,5" in French).
   * `number` and `setNumber` always use the '.' form either way.
   */
  inputType?: 'number' | 'decimal';
  textSize?: string | number;
  textAlign?: 'left' | 'center' | 'right';
  InputProps?: any;
  required?: boolean;
  max?: number;
  min?: number;
  unsigned?: boolean;
  size?: 'small' | 'medium';
  lockMessage?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  fullWidth?: boolean;
}
export const NumberField = ({
  number,
  setNumber,
  label,
  onBlur,
  error,
  sx,
  infoTooltip,
  disabled,
  locked,
  testId,
  decimalPlaces = 2,
  unlimitedDecimals = false,
  trailingIcon = undefined,
  showArrows = true,
  textSize = '16px',
  textAlign = 'left',
  InputProps,
  required = false,
  max,
  min,
  errorText,
  unsigned = false,
  size = 'medium',
  lockMessage,
  onKeyDown,
  fullWidth = true,
  inputType = 'number',
  inputProps,
  ...rest
}: NumberFieldProps) => {
  const [displayValue, setDraft] = useNumericInputDraft(number);
  const isDecimal = inputType === 'decimal';
  const { decimalSeparator } = getLocaleNumberSeparators();
  const shownValue = isDecimal
    ? displayValue.replace('.', decimalSeparator)
    : displayValue;

  const emit = (value: string) => {
    setDraft(value);
    setNumber(value);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.target;
    // A number input reports '' once its text stops parsing ("2.002-"), but keeps
    // the text on screen. React then agrees the value is '' and never writes, so
    // the field shows a quantity the form does not hold. Reassigning drops it.
    if (input.validity?.badInput) {
      input.value = displayValue;
    }
    onBlur?.(event);
  };

  return (
    <TextField
      testId={testId ?? 'number-field'}
      onKeyDown={onKeyDown}
      onChange={(e: any) => {
        const value: string | null = isDecimal
          ? normalizeDecimalInput(e.target.value, decimalSeparator)
          : e.target.value;
        if (value === null) {
          return;
        }

        if (typeof max === 'number' && value !== '') {
          if (parseFloat(value) > max) {
            return;
          }
        }
        if (typeof min === 'number' && value !== '') {
          if (parseFloat(value) < min) {
            return;
          }
        }

        if (unsigned && value.includes('-')) {
          return;
        }
        if (!unlimitedDecimals && value.includes('.')) {
          const [whole, decimal] = value.split('.');
          // A whole-number field has no fractional part, so the separator goes
          // too — otherwise the field is left showing a dangling "12.".
          if (decimalPlaces === 0) {
            emit(whole);
            return;
          }
          if (decimal.length > decimalPlaces) {
            emit(`${whole}.${decimal.slice(0, decimalPlaces)}`);
            return;
          }
        }
        emit(value);
      }}
      label={label ?? t('components:number')}
      fullWidth={fullWidth}
      lockMessage={lockMessage ?? t('pageConfiguration:notEditableMessage')}
      disabled={disabled}
      locked={locked}
      value={shownValue}
      decimalPlaces={decimalPlaces}
      unlimitedDecimals={unlimitedDecimals}
      placeholder={
        isDecimal
          ? getDecimalPlaceholder(decimalPlaces)
          : decimalPlaces
            ? `0.${'0'.repeat(decimalPlaces)}`
            : '0'
      }
      type={isDecimal ? 'default' : 'number'}
      inputProps={
        isDecimal ? { inputMode: 'decimal', ...inputProps } : inputProps
      }
      onBlur={handleBlur}
      error={error}
      errorText={errorText}
      sx={sx}
      infoTooltip={infoTooltip}
      trailingIcon={trailingIcon}
      showArrows={showArrows}
      textSize={textSize}
      textAlign={textAlign}
      size={size}
      InputProps={InputProps}
      required={required}
      {...rest}
    />
  );
};
